import { useState } from "react";
import { ListFilter as ListFilterIcon } from "@tamagui/lucide-icons";
import { Body2, Button, Checkbox, hairline, Popover, Subtitle1, XStack, YStack } from "@medbillai/ui";
import { analyticsService } from "../../../lib/analytics";
import type { CaseListFilterType } from "../../../lib/analytics/types";
import type { CaseListFilters } from "../utils/types";
type CaseListFilterPopoverProps = CaseListFilters & {
  onFilterChange: (filters: CaseListFilters) => void;
};
export const CaseListFilterPopover = ({
  showRead,
  showUnread,
  showClosed,
  onFilterChange
}: CaseListFilterPopoverProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const showOpen = showRead && showUnread;
  const handleFilterChange = (filterType: CaseListFilterType, value: boolean) => {
    let nextRead = showRead;
    let nextUnread = showUnread;
    let nextClosed = showClosed;
    if (filterType === "open") {
      nextRead = value;
      nextUnread = value;
    } else if (filterType === "closed") {
      nextClosed = value;
    } else if (filterType === "read") {
      nextRead = value;
    } else if (filterType === "unread") {
      nextUnread = value;
    }

    // Prevent turning off all filters
    if (!nextRead && !nextUnread && !nextClosed) {
      return;
    }
    analyticsService.trackEvent("CasesFilterChanged", {
      filterType,
      enabled: value,
      resultingFilters: {
        showRead: nextRead,
        showUnread: nextUnread,
        showClosed: nextClosed
      }
    });
    onFilterChange({
      showRead: nextRead,
      showUnread: nextUnread,
      showClosed: nextClosed
    });
  };
  return <Popover size="$si4" allowFlip placement="bottom-end" open={popoverOpen} onOpenChange={setPopoverOpen} data-sentry-element="Popover" data-sentry-component="CaseListFilterPopover" data-sentry-source-file="CaseListFilterPopover.tsx">
      <Popover.Trigger asChild data-sentry-element="Popover.Trigger" data-sentry-source-file="CaseListFilterPopover.tsx">
        <Button variant="tertiary" size="small" fillWidth={false} icon={ListFilterIcon} data-sentry-element="Button" data-sentry-source-file="CaseListFilterPopover.tsx" />
      </Popover.Trigger>
      <Popover.Content borderWidth={hairline} borderColor="$strokeSecondary" enterStyle={{
      scale: 0.85,
      opacity: 0,
      y: -6
    }} exitStyle={{
      scale: 0.85,
      opacity: 0,
      y: -6
    }} width={200} elevate transformOrigin="right top" transition="150ms easeOut" padding="$sp2.5" data-sentry-element="Popover.Content" data-sentry-source-file="CaseListFilterPopover.tsx">
        <YStack gap="$sp1.5" alignItems="flex-start" width="100%" data-sentry-element="YStack" data-sentry-source-file="CaseListFilterPopover.tsx">
          <Subtitle1 color="$textSecondary" data-sentry-element="Subtitle1" data-sentry-source-file="CaseListFilterPopover.tsx">Filter cases</Subtitle1>

          <XStack gap="$sp1.5" alignItems="center" onPress={() => handleFilterChange("open", !showOpen)} cursor="pointer" data-sentry-element="XStack" data-sentry-source-file="CaseListFilterPopover.tsx">
            <Checkbox checked={showOpen} onCheckedChange={checked => handleFilterChange("open", checked === true)} data-sentry-element="Checkbox" data-sentry-source-file="CaseListFilterPopover.tsx" />
            <Body2 data-sentry-element="Body2" data-sentry-source-file="CaseListFilterPopover.tsx">Open</Body2>
          </XStack>

          <XStack gap="$sp1" alignItems="center" onPress={() => handleFilterChange("unread", !showUnread)} cursor="pointer" paddingLeft="$sp2" data-sentry-element="XStack" data-sentry-source-file="CaseListFilterPopover.tsx">
            <Checkbox checked={showUnread} onCheckedChange={checked => handleFilterChange("unread", checked === true)} data-sentry-element="Checkbox" data-sentry-source-file="CaseListFilterPopover.tsx" />
            <Body2 data-sentry-element="Body2" data-sentry-source-file="CaseListFilterPopover.tsx">Unread</Body2>
          </XStack>

          <XStack gap="$sp1" alignItems="center" onPress={() => handleFilterChange("read", !showRead)} cursor="pointer" paddingLeft="$sp2" data-sentry-element="XStack" data-sentry-source-file="CaseListFilterPopover.tsx">
            <Checkbox checked={showRead} onCheckedChange={checked => handleFilterChange("read", checked === true)} data-sentry-element="Checkbox" data-sentry-source-file="CaseListFilterPopover.tsx" />
            <Body2 data-sentry-element="Body2" data-sentry-source-file="CaseListFilterPopover.tsx">Read</Body2>
          </XStack>

          <XStack gap="$sp1" alignItems="center" onPress={() => handleFilterChange("closed", !showClosed)} cursor="pointer" data-sentry-element="XStack" data-sentry-source-file="CaseListFilterPopover.tsx">
            <Checkbox checked={showClosed} onCheckedChange={checked => handleFilterChange("closed", checked === true)} data-sentry-element="Checkbox" data-sentry-source-file="CaseListFilterPopover.tsx" />
            <Body2 data-sentry-element="Body2" data-sentry-source-file="CaseListFilterPopover.tsx">Closed</Body2>
          </XStack>
        </YStack>
      </Popover.Content>
    </Popover>;
};